import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

const EosbPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>();
  const { empLabel } = useEmployeeOptions({ employment_status: null });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.eosbSettlements(),
    queryFn: () => hrmsService.listEosbSettlements(),
  });

  const filtered = useMemo(
    () => (statusFilter ? rows.filter((r: any) => r.status === statusFilter) : rows),
    [rows, statusFilter]
  );

  const stats = useMemo(() => {
    const pending = rows.filter((r: any) => r.status === 'Draft' || r.status === 'Pending').length;
    const payable = rows.reduce((s: number, r: any) => s + Number(r.net_payable || 0), 0);
    return { total: rows.length, pending, payable };
  }, [rows]);

  const columns: ColumnsType<any> = [
    {
      title: 'Settlement #',
      dataIndex: 'settlement_number',
      width: 140,
      fixed: 'left',
      render: (v: string, r) => <a onClick={() => navigate(`/hr/eosb/${r.id}`)}>{v || 'Open'}</a>,
    },
    { title: 'Employee', dataIndex: 'employee_id', ellipsis: true, render: (id: string) => empLabel(id) },
    { title: 'Exit type', dataIndex: 'exit_type', width: 130 },
    {
      title: 'Last working',
      dataIndex: 'last_working_date',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    { title: 'Gratuity', dataIndex: 'gratuity_amount', width: 110 },
    { title: 'Net payable', dataIndex: 'net_payable', width: 120 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 90,
      render: (_, r) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/hr/eosb/${r.id}`)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="EOSB settlements"
          subtitle="End-of-service benefit calculation and settlements"
          primaryActions={
            <Space>
              <Button icon={<UserOutlined />} onClick={() => navigate('/hr/eosb/profile')}>
                Employee profile
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/eosb/new')}>
                New settlement
              </Button>
            </Space>
          }
        />
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Total" value={stats.total} /></Card></Col>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Draft / Pending" value={stats.pending} /></Card></Col>
        <Col xs={24} sm={8}><Card size="small"><Statistic title="Net payable" value={stats.payable} precision={2} /></Card></Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={['Draft', 'Pending', 'Approved', 'Paid', 'Closed'].map((v) => ({ value: v, label: v }))}
          />
          <Text type="secondary">{filtered.length} record(s)</Text>
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={filtered}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 20 }}
        onRow={(r) => ({ onClick: () => navigate(`/hr/eosb/${r.id}`), style: { cursor: 'pointer' } })}
      />
    </WorkspaceLayout>
  );
};

export default EosbPage;
